export interface StoredAuthTokens {
  accessToken: string;
  refreshToken: string;
}

export const AUTH_TOKENS_CHANGED_EVENT = "dealhawk:auth-tokens-changed";

const AUTH_TOKEN_STORAGE_KEYS = new Set([
  "dealhawk_access_token",
  "dealhawk_refresh_token",
]);

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
}

function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
}

function setCookie(name: string, value: string) {
  document.cookie = `${name}=${value}; path=/; SameSite=Lax`;
}

function notifyAuthTokensChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_TOKENS_CHANGED_EVENT));
  }
}

export function subscribeToAuthTokenChanges(listener: () => void) {
  if (typeof window === "undefined") return () => {};

  const handleStorage = (event: StorageEvent) => {
    if (event.key === null || AUTH_TOKEN_STORAGE_KEYS.has(event.key)) {
      listener();
    }
  };

  window.addEventListener(AUTH_TOKENS_CHANGED_EVENT, listener);
  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener(AUTH_TOKENS_CHANGED_EVENT, listener);
    window.removeEventListener("storage", handleStorage);
  };
}

export function saveCredsInLocalStorage(
  accessToken: string,
  refreshToken: string
) {
  try {
    localStorage.setItem("dealhawk_access_token", accessToken);
    localStorage.setItem("dealhawk_refresh_token", refreshToken);
  } catch {
    setCookie("dealhawk_access_token", accessToken);
    setCookie("dealhawk_refresh_token", refreshToken);
  }
  notifyAuthTokensChanged();
}

export function getTokensFromStorage(): StoredAuthTokens | null {
  try {
    const accessToken = localStorage.getItem("dealhawk_access_token");
    const refreshToken = localStorage.getItem("dealhawk_refresh_token");
    if (!accessToken || !refreshToken) return null;
    return { accessToken, refreshToken };
  } catch {
    const accessToken = getCookie("dealhawk_access_token");
    const refreshToken = getCookie("dealhawk_refresh_token");
    if (!accessToken || !refreshToken) return null;
    return { accessToken, refreshToken };
  }
}

export function clearAuthTokens() {
  try {
    localStorage.removeItem("dealhawk_access_token");
    localStorage.removeItem("dealhawk_refresh_token");
  } catch {
    // Cookie cleanup below remains available when storage is inaccessible.
  }
  setCookie("dealhawk_access_token", "");
  setCookie("dealhawk_refresh_token", "");
  notifyAuthTokensChanged();
}

export function isAccessTokenExpired(accessToken: string, bufferMs = 30_000) {
  try {
    const encodedPayload = accessToken.split(".")[1];
    if (!encodedPayload) return true;
    const base64 = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const payload = JSON.parse(window.atob(padded)) as {
      expires?: number;
      exp?: number;
    };
    const expiresAt =
      typeof payload.expires === "number"
        ? payload.expires
        : typeof payload.exp === "number"
          ? payload.exp * 1000
          : undefined;
    return expiresAt === undefined || Date.now() + bufferMs >= expiresAt;
  } catch {
    return true;
  }
}

export function isRefreshTokenRejected(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("status" in error)) {
    return false;
  }
  const status = (error as { status?: number }).status;
  return status === 400 || status === 401 || status === 403;
}

let refreshPromise: Promise<string> | null = null;

export function refreshAccessToken(refreshToken: string): Promise<string> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const response = await fetch(import.meta.env.VITE_API_URL + "/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) {
      const error = new Error("Unable to refresh session");
      (error as Error & { status: number }).status = response.status;
      throw error;
    }

    const tokens = (await response.json()) as RefreshResponse;
    if (!tokens.access_token || !tokens.refresh_token) {
      const error = new Error("Refresh response did not contain tokens");
      (error as Error & { status: number }).status = 502;
      throw error;
    }
    saveCredsInLocalStorage(tokens.access_token, tokens.refresh_token);
    return tokens.access_token;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}
